/**
 * Every guard awaits `bootstrap()` first, and that is the whole reason they are
 * async.
 *
 * On a hard refresh the app has an SSO cookie and no session yet. Deciding
 * before the exchange has happened would bounce a perfectly signed-in user to
 * the login screen on every F5. `bootstrap()` is single-flight and settles
 * once, so several guards awaiting it cost one request between them.
 *
 * None of this is a security control. It decides what to render; the server
 * re-checks authentication, the admin role and every grant on every request.
 */
function redirectToSignIn(to, options) {
    return { path: options.signIn, query: { [options.redirectQueryKey ?? 'redirect']: to.fullPath } };
}
/** Lets a signed-in user through, and sends anyone else to sign in with their destination remembered. */
export function createAuthGuard(session, options) {
    return async (to) => {
        await session.bootstrap();
        return session.isAuthenticated.value ? true : redirectToSignIn(to, options);
    };
}
/** Admin-only routes. Being signed in is not enough. */
export function createAdminGuard(session, options) {
    return async (to) => {
        await session.bootstrap();
        if (!session.isAuthenticated.value)
            return redirectToSignIn(to, options);
        return session.isAdmin.value ? true : options.forbidden;
    };
}
/** Routes belonging to one application, for the SPAs that are not identity. */
export function createGrantGuard(session, options) {
    return async (to) => {
        await session.bootstrap();
        if (!session.isAuthenticated.value)
            return redirectToSignIn(to, options);
        return session.can(options.app) ? true : options.forbidden;
    };
}
/**
 * The inverse: sign-in, signup and password-reset screens, which a signed-in
 * user has no business seeing. Without this, following a stale bookmark to
 * `/login` shows a form that would open a second session.
 */
export function createGuestGuard(session, options) {
    return async () => {
        await session.bootstrap();
        return session.isAuthenticated.value ? options.home : true;
    };
}
//# sourceMappingURL=guards.js.map